import { bool } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import BarChart from '@components/charts/BarChart';

import useTrendResearchFilterParams from '@features/trend-research/hooks/useTrendResearchFilterParams';
import TrendResearchDoughnutChart from '@features/trend-research/components/common/TrendResearchDoughnutChart';
import OtherMarkets from '@features/trend-research/components/onboarding/components/OtherMarkets';
import ProductCountBarChart from '@features/trend-research/components/onboarding/components/ProductCountBarChart';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchDepartments } from '@services/commonService';
import { findBrandInsightSummary, findBrandInsightSummaryByCategoryAndMarket } from '@services/trendResearch';

import { capitalizeFirstLetter, getNumberUnit, trendResearchColorSchema } from '@utils/Util';

const ProductInformationCharts = ({ isSummaryCountSet }) => {
	const { queryParams } = useTrendResearchFilterParams([
		'fabricTypeList',
		'marketIdList',
		'categoryIdList',
		'instagramUserNameList'
	]);
	const dispatch = useDispatch();
	const [summary, setSummary] = useState({});
	const [markets, setMarkets] = useState([]);
	const [selectedMarkets, setSelectedMarkets] = useState([]);
	const [categoryAndMarketWiseSummary, setCategoryAndMarketWiseSummary] = useState({});

	const fetchMarkets = async () => {
		try {
			const data = await fetchDepartments();
			setMarkets(data);
		} catch {
			/* empty */
		}
	};

	useEffect(() => {
		if (markets.length > 0) {
			setSelectedMarkets(markets);
		}
	}, [markets]);

	useEffect(() => {
		(async () => {
			await fetchMarkets();
		})();
	}, []);

	const toggleSelectedMarket = (item) => {
		const isExist = selectedMarkets.find((market) => market.id === item.id);
		if (isExist) {
			let modifiedList = selectedMarkets.filter((market) => market.id !== item.id);
			setSelectedMarkets(modifiedList);
		} else {
			setSelectedMarkets((prev) => [...prev, item]);
		}
	};

	const getBrandInsightByCategoryAndMarket = async (queryParams) => {
		try {
			const data = await findBrandInsightSummaryByCategoryAndMarket(queryParams);
			setCategoryAndMarketWiseSummary(data);
		} catch {
			/* empty */
		}
	};

	const getBrandInsight = async (queryParams) => {
		try {
			const data = await findBrandInsightSummary(queryParams);
			setSummary(data);
		} catch {
			/* empty */
		}
	};

	useEffect(() => {
		if (isSummaryCountSet && queryParams !== null) {
			(async () => {
				dispatch(openLoader());
				await getBrandInsight(queryParams);
				dispatch(closeLoader());
			})();
		}
	}, [queryParams, isSummaryCountSet]);

	useEffect(() => {
		if (isSummaryCountSet && queryParams !== null) {
			(async () => {
				dispatch(openLoader());
				let modifiedParams = queryParams;
				let marketListParams = selectedMarkets.map((item) => item.name).join(',');
				if (modifiedParams.length === 0) {
					modifiedParams += `?marketList=${marketListParams}`;
				} else {
					modifiedParams += `&marketList=${marketListParams}`;
				}
				await getBrandInsightByCategoryAndMarket(modifiedParams);
				dispatch(closeLoader());
			})();
		}
	}, [queryParams, isSummaryCountSet, selectedMarkets]);

	const renderFabricWiseMarketDistribution = () => {
		if (
			summary?.fabricWiseProductCountMap &&
			Object.keys(summary.fabricWiseProductCountMap)?.length > 0
		) {
			const chartLabels = Object.keys(summary.fabricWiseProductCountMap)?.map((item) =>
				capitalizeFirstLetter(item.replace('_', '-'))
			);
			const dataSets = [
				{
					label: 'Brand',
					data: Object.values(summary.fabricWiseProductCountMap),
					backgroundColor: 'rgba(81, 157, 233, 1)',
					borderRadius: 5,
					barPercentage: 1,
					categoryPercentage: 0.5
				}
			];
			return (
				<BarChart
					chartData={dataSets}
					chartLabels={chartLabels}
					optional={{
						aspectRatio: 2.2,
						indexAxis: 'y',
						scales: {
							x: {
								grid: {
									display: false
								}
							},
							y: {
								grid: {
									display: false
								}
							}
						},
						plugins: {
							legend: {
								display: false
							},
							tooltip: {
								callbacks: {
									title: () => '',
									label: function (tooltipItem) {
										return `${getNumberUnit(tooltipItem.raw)}`;
									}
								}
							}
						}
					}}
				/>
			);
		}
	};

	const renderProductCountsByCategoryAndMarket = () => {
		const chartLabels =
			markets?.map((item, idx) => ({
				fill: trendResearchColorSchema[idx],
				dataKey: item.name,
				title: item.name
			})) || [];

		const chartData =
			Object.keys(categoryAndMarketWiseSummary)?.map((item) => {
				let tmpObj = {};
				Object.keys(categoryAndMarketWiseSummary[item]).forEach((data) => {
					tmpObj[data] = categoryAndMarketWiseSummary[item][data];
				});
				return { ...tmpObj, name: capitalizeFirstLetter(item) };
			}) || [];

		return (
			<div className="prodcut-category-count-chart radius-4">
				<div className="chart-header d-flex align-items-center justify-content-between">
					<p className="regular-14 weight-500 gray_dark_01">Category wise distribution</p>
					<div className=" dot-and-dropdown d-flex align-items-center">
						<div className="perfomence-list d-flex flex-wrap">
							{selectedMarkets?.map((item, idx) => (
								<div
									className="dot-and-name d-flex align-items-center"
									key={`product_market_${item.id}`}
								>
									<span
										className="dots"
										style={{ backgroundColor: trendResearchColorSchema[idx] }}
									/>
									<span className="cate-name regular-14 gray_dark_02 weight-500">
										{item.name}
									</span>
								</div>
							))}
						</div>
						<OtherMarkets
							selectedMarkets={selectedMarkets}
							marketList={markets}
							toggleSelectedMarket={toggleSelectedMarket}
						/>
					</div>
				</div>
				<div className="chart-body m-auto text-center p-4 scroll-x-label w-100">
					<ProductCountBarChart chartData={chartData} chartLabels={chartLabels} />
				</div>
			</div>
		);
	};

	const processGraphicsLabels = () => {
		const updatedGraphicsMap = {};
		if (summary?.graphicsTypeWisePercentageMap) {
			Object.keys(summary.graphicsTypeWisePercentageMap).forEach((key) => {
				let newKey = key.replace('and', '&');
				if (newKey === 'Print & Embroidery') {
					newKey = 'Print & Emb';
				}
				updatedGraphicsMap[newKey] = summary.graphicsTypeWisePercentageMap[key];
			});
		}
		return updatedGraphicsMap;
	};

	return (
		<>
			<div className="chart-and-graph-section">
				<div className="chart-graph-grid">
					<TrendResearchDoughnutChart
						title={'Department wise distribution'}
						dataSet={summary.marketPercentageMap}
					/>
					<div className="product-chart-box radius-4">
						<div className="chart-header">
							<p className="regular-14 weight-500 gray_dark_01">Fabric wise distribution</p>
						</div>
						<div className="chart-body m-auto text-center h-100 d-flex">
							<div className="d-flex align-items-center w-100">
								<div className="m-auto text-center w-100">
									{renderFabricWiseMarketDistribution()}
								</div>
							</div>
						</div>
					</div>
					<TrendResearchDoughnutChart
						title={'Solid vs Embellishment'}
						dataSet={processGraphicsLabels()}
					/>
				</div>
			</div>
			{renderProductCountsByCategoryAndMarket()}
		</>
	);
};

ProductInformationCharts.propTypes = {
	isSummaryCountSet: bool.isRequired
};

export default ProductInformationCharts;
